import { Box, HStack, type StackProps } from '@chakra-ui/react';
import { Fragment, type ReactNode } from 'react';
import { Tooltip } from './Tooltip';

/**
 * SegmentedControl — rectangular trough + raised thumb for mutually exclusive
 * modes (All / Parts, Dollar / Percent). Geometry comes from the settings
 * redesign (PR #448 SettingsSegmentedControl); color is Yaxle tokens so a
 * rebrand does not edit call sites.
 *
 * Track is `bg.subtle` (`lg` radius). The selected segment is `bg.surface`
 * with a hairline (`border.default`, `md` radius). Always-on 1px border so
 * selecting a segment does not shift width. No pill radius, no drop shadow.
 *
 * Accepts `items` or `segments` so settings can drop `SettingsSegmentedControl`
 * and import this primitive on rebase.
 */
export interface SegmentItem<T extends string = string> {
	value: T;
	label: ReactNode;
	tooltip?: string;
	isDisabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string>
	extends Omit<StackProps, 'onChange'> {
	items?: ReadonlyArray<SegmentItem<T>>;
	/** Alias for `items` — settings PR #448 used `segments`. */
	segments?: ReadonlyArray<SegmentItem<T>>;
	value: T;
	onChange: (value: T) => void;
	size?: 'sm' | 'md';
	/** Stretch the track to its container and split it evenly per segment. */
	fullWidth?: boolean;
}

export function SegmentedControl<T extends string = string>({
	items,
	segments,
	value,
	onChange,
	size = 'sm',
	fullWidth = false,
	...rest
}: SegmentedControlProps<T>) {
	const options = items ?? segments ?? [];
	const isSmall = size === 'sm';
	return (
		<HStack
			spacing={0}
			bg="bg.subtle"
			borderRadius="lg"
			p="2px"
			flexShrink={0}
			display={fullWidth ? 'flex' : 'inline-flex'}
			w={fullWidth ? '100%' : undefined}
			role="radiogroup"
			{...rest}
		>
			{options.map((item) => {
				const isActive = item.value === value;
				const isDisabled = item.isDisabled === true;
				const button = (
					<Box
						as="button"
						type="button"
						role="radio"
						aria-checked={isActive}
						onClick={() => {
							if (!isDisabled) onChange(item.value);
						}}
						disabled={isDisabled}
						cursor={isDisabled ? 'not-allowed' : 'pointer'}
						flex={fullWidth ? '1' : undefined}
						textAlign="center"
						px={isSmall ? 3 : 4}
						py={isSmall ? 1 : 1.5}
						borderRadius="md"
						borderWidth="1px"
						borderColor={isActive ? 'border.default' : 'transparent'}
						bg={isActive ? 'bg.surface' : 'transparent'}
						boxShadow="none"
						color={isDisabled ? 'fg.subtle' : isActive ? 'fg.default' : 'fg.muted'}
						fontSize={isSmall ? 'sm' : 'md'}
						fontWeight={isActive ? 'semibold' : 'medium'}
						lineHeight={1.4}
						whiteSpace="nowrap"
						transition="background 0.15s ease, color 0.15s ease, border-color 0.15s ease"
						_hover={{
							color: isDisabled || isActive ? undefined : 'fg.default',
						}}
					>
						{item.label}
					</Box>
				);
				return (
					<Fragment key={item.value}>
						{item.tooltip ? (
							<Tooltip label={item.tooltip} placement="top" hasArrow fontSize="sm">
								{isDisabled ? (
									<Box as="span" display="inline-flex">
										{button}
									</Box>
								) : (
									button
								)}
							</Tooltip>
						) : (
							button
						)}
					</Fragment>
				);
			})}
		</HStack>
	);
}

export default SegmentedControl;
